import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Code2, FileCode, BookOpen, Landmark, Receipt, HandCoins, ShieldCheck } from "lucide-react";

// ---------------------------------------------------------------------
// Same theme tokens as Navbar.jsx — no new colors introduced, just a
// new dropdown following the same two-column category -> linked-cards
// structure shown in the reference screenshots.
// ---------------------------------------------------------------------
const theme = {
  accent: "#E63946",
  text: "#FFF8F6",
  textMuted: "#D8B4B8",
  panelBg: "#FFFFFF",
  panelInk: "#221619",
  panelInkMuted: "#6B6265",
  cardBg: "#F6EEEC",
};

const categories = [
  {
    label: "Documentation and Guides",
    items: [
      { icon: Code2, title: "Developer API", href: "https://www.instantpay.in/developers-hub/documentation-and-guide/developer-api" },
      { icon: FileCode, title: "API Documentation", href: "https://developers.instantpay.in/docs/getting-started" },
      { icon: BookOpen, title: "Integration Guide", href: "https://developers.instantpay.in/docs/getting-started" },
    ],
  },
  {
    label: "Developer API",
    items: [
      { icon: Receipt, title: "Automate Your Payouts", href: "https://developers.instantpay.in/reference/payouts-api" },
      { icon: Receipt, title: "Manage & Pay All Bills", href: "https://developers.instantpay.in/reference/marketplace-utility-bill-payments-overview" },
      { icon: Landmark, title: "Banking Services", href: "https://developers.instantpay.in/reference/banking-overview" },
      { icon: HandCoins, title: "Collect Money from Customers", href: "https://developers.instantpay.in/reference/collect-overview" },
      { icon: ShieldCheck, title: "Prevent Fraud and Identity Theft", href: "https://developers.instantpay.in/reference/identity-verification-overview" },
    ],
  },
];

export default function DevelopersHubMenu() {
  const [open, setOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(0);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <li className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        data-cursor="hover"
        style={{ color: open ? theme.text : theme.textMuted }}
        className="flex items-center gap-1.5 py-1 transition-colors hover:text-[#FFF8F6]"
      >
        Developer's Hub
        <ChevronDown size={14} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute right-0 top-full mt-3 rounded-2xl shadow-2xl overflow-hidden grid grid-cols-[220px_1fr]"
            style={{ backgroundColor: theme.panelBg, width: 560 }}
          >
            {/* left: categories */}
            <div className="p-5 flex flex-col gap-1" style={{ backgroundColor: theme.cardBg }}>
              {categories.map((cat, i) => {
                const isActive = i === activeCat;
                return (
                  <button
                    key={cat.label}
                    data-cursor="hover"
                    onMouseEnter={() => setActiveCat(i)}
                    className="relative text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-colors duration-200"
                    style={{
                      backgroundColor: isActive ? theme.panelBg : "transparent",
                      color: isActive ? theme.panelInk : theme.panelInkMuted,
                      boxShadow: isActive ? "0 4px 14px rgba(34,22,25,0.08)" : "none",
                    }}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* right: linked cards for the active category */}
            <div className="p-5">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCat}
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.18 }}
                  className="flex flex-col gap-2"
                >
                  {categories[activeCat].items.map((item) => (
                    
               <a   key={item.title}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-cursor="hover"
                      className="group flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 hover:bg-[#F6EEEC]"
                    >
                      <span
                        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-105"
                        style={{ backgroundColor: "rgba(230,57,70,0.1)", color: theme.accent }}
                      >
                        <item.icon size={17} />
                      </span>
                      <span className="text-sm font-semibold" style={{ color: theme.panelInk }}>
                        {item.title}
                      </span>
                    </a>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}